import { ERROR_CODES, PRINTER_TYPES, isValidIPAddress, isValidCmd } from "./Utils";
import PrinterError from "./PrinterError";

// Function to send the print command to the printer via TCP/IP
const sendToNetworkPrinter = async (printerIP, command) => {
    const printerPort = 9100;

    // Validate input parameters
    if (!printerIP || !isValidIPAddress(printerIP)) {
        throw new PrinterError(ERROR_CODES.INVALID_INPUT, "Invalid printer IP address.");
    }
    if (!isValidCmd(command)) {
        throw new PrinterError(ERROR_CODES.INVALID_INPUT, "Command must be a Uint8Array.");
    }

    return new Promise((resolve, reject) => {
        // eslint-disable-next-line no-undef
        const socket = new Socket();

        const closeSocket = () => {
            try {
                socket.close(); // Fully close the socket to release resources
            } catch (err) {
                console.error(`Error closing socket: ${err.message}`);
            }
        };

        // Handle successful socket connection
        socket.open(
            printerIP,
            printerPort,
            () => {
                try {
                    socket.write(
                        command,
                        () => {
                            closeSocket();
                            resolve(true);
                        },
                        (errorMessage) => {
                            const error = `IP Printer write error: ${errorMessage}`;
                            console.error(error);
                            closeSocket();
                            reject(new PrinterError(ERROR_CODES.UNKNOWN, error));
                        }
                    );
                } catch (err) {
                    const error = `Unexpected error during write: ${err.message}`;
                    console.error(error);
                    closeSocket();
                    reject(new PrinterError(ERROR_CODES.UNKNOWN, error));
                }
            },
            (errorMessage) => {
                // Handle connection errors
                const error = `IP Printer connection error: ${errorMessage}`;
                console.error(error);
                reject(new PrinterError(ERROR_CODES.CONNECTION_ERROR, error));
            }
        );
    });
};

// Function to send the print command to the printer via Bluetooth
const sendToBluetoothPrinter = async (deviceId, command) => {
    if (!deviceId) {
        throw new PrinterError(ERROR_CODES.INVALID_INPUT, "Invalid printer ID.");
    }
    if (!isValidCmd(command)) {
        throw new PrinterError(ERROR_CODES.INVALID_INPUT, "Command must be a Uint8Array.");
    }

    return new Promise((resolve, reject) => {
        const bluetoothSerial = window.bluetoothSerial;

        // Check if BluetoothSerial plugin is available
        if (!bluetoothSerial) {
            return reject(new PrinterError(ERROR_CODES.CONNECTION_ERROR, "Bluetooth is disabled or unavailable."));
        }

        // Function to write the command to the connected device
        const handleWrite = () => {
            bluetoothSerial.write(
                command,
                () => resolve(true),
                (err) => reject(new PrinterError(ERROR_CODES.UNKNOWN, `Bluetooth Write Error: ${err}`))
            );
        };

        // Check if the device is already connected
        bluetoothSerial.isConnected(
            handleWrite,
            () => {
                // If `isConnected` fails, first check if Bluetooth is off
                bluetoothSerial.isEnabled(
                    () => {
                        // Bluetooth is enabled, but not connected, so attempt to connect
                        bluetoothSerial.connect(
                            deviceId,
                            handleWrite,
                            (connectErr) => reject(new PrinterError(ERROR_CODES.CONNECTION_ERROR, `Bluetooth Connect Error: ${connectErr}`))
                        );
                    },
                    () => {
                        // Bluetooth is off, reject with a proper error
                        reject(new PrinterError(ERROR_CODES.CONNECTION_ERROR, "Bluetooth is turned off."));
                    }
                );
            }
        );
    });
};

// get bluetooth devices
const getBluetoothDevices = () => {
    const bluetoothSerial = window.bluetoothSerial;
    // Check if BluetoothSerial plugin is available
    if (!bluetoothSerial) {
        return [];
    }
    return new Promise((resolve) => {
        bluetoothSerial.list(
            (devices) => {
                let printers = [];
                devices.forEach((device) => {
                    printers.push({
                        id: device.id,
                        name: device.name,
                    })
                })
                resolve(printers);
            },
            () => resolve([])
        );
    })
}

// get Usb devices
const getUsbDevices = () => {
    const USBPlugin = window['cordova'].plugins.UsbPrinter;
    // Check if BluetoothSerial plugin is available
    if (!USBPlugin) {
        return [];
    }
    return new Promise((resolve) => {
        USBPlugin.list((devices) => {
            let printers = [];
            devices.forEach((device) => {
                printers.push({
                    id: device.printername,
                    name: device.productName,
                })
            })
            resolve(printers);
        }, err => {
            console.error(err);
            resolve([]);
            // failure callback execution
        });
    })
}

// get Usb devices
const sendToUsbPrinter = (deviceId, command) => {
    const USBPlugin = window['cordova'].plugins.UsbPrinter;
    // Check if BluetoothSerial plugin is available
    if (!USBPlugin) {
        return [];
    }
    return new Promise((resolve, reject) => {
        USBPlugin.connect(deviceId, () => {
            // success callback execution
            USBPlugin.write(deviceId, command, (result) => {
                console.log("result of usb print sendCommand", result)
                resolve(true)
                // successful callback execution
            }, err => {
                console.error('Error in usb print sendCommand', err)
                // failure callback execution
                reject(new PrinterError(ERROR_CODES.INVALID_INPUT, err));
            });

        }, err => {
            // use this method to recognise the disconnection of usb device
            console.error(err)
            reject(new PrinterError(ERROR_CODES.UNKNOWN, err));
            // failure callback execution
        });
    });

}


// Main function to handle the printing logic
const cordovaPrinter = {
    sendToNetworkPrinter,
    sendToBluetoothPrinter,
    getBluetoothDevices,
    getUsbDevices,
    sendToUsbPrinter,
    getSupportedPrinterTypes() {
        return [
            PRINTER_TYPES.NO_PRINT,
            // PRINTER_TYPES.NETWORK,
            // PRINTER_TYPES.BLUETOOTH,
            // PRINTER_TYPES.USB,
        ]
    }
};

// Export the cordovaPrinter object to be used in other parts of the application
export default cordovaPrinter;
